/**
 * Scenario registry + loader. This is the seam that makes sim3d MULTI-COURSE
 * and is the integration point for Cyberfrog: today scenarios are static specs
 * compiled in; tomorrow a Cyberfrog `interactive_3d` training serves the same
 * ScenarioSpec JSON from its own endpoint and we load it by URL.
 *
 * Selection precedence (highest first):
 *   1. ?spec=<url>        -> fetch a remote ScenarioSpec JSON (Cyberfrog hosting)
 *   2. ?scenario=<id>     -> a built-in spec from REGISTRY
 *   3. DEFAULT_SCENARIO_ID
 *
 * Nothing here references React/Three so it ports into Cyberfrog unchanged.
 */
import type { ScenarioSpec } from "./types";
import { getHostConfig } from "../lib/hostConfig";
import { phishingOfficeScenario } from "./phishing-office.scenario";
import { deepfakeWhalingScenario } from "./deepfake-whaling.scenario";
import { socialEngineeringScenario } from "./social-engineering.scenario";
import { quishingRestaurantScenario } from "./quishing-restaurant.scenario";
import { ransomwareOfficeScenario } from "./ransomware-office.scenario";
import { spearPhishingOsintScenario } from "./spear-phishing-osint.scenario";
import { remoteWorkMaxScenario } from "./remote-work-max.scenario";
import { officeWalkableScenario } from "./office-walkable.scenario";
import { officeUnderAttackScenario } from "./office-under-attack.scenario";
import { poolWalkableScenario } from "./pool-walkable.scenario";
import { holidayCallScenario } from "./holiday-call.scenario";
import { pcToDarkWebScenario } from "./pc-to-darkweb.scenario";
import { dataHandlingScenario } from "./data-handling.scenario";
import { spotPhishingScenario } from "./spot-phishing.scenario";
import { workplacePoliciesScenario } from "./workplace-policies.scenario";
import { insideTheBankScenario } from "./inside-the-bank.scenario";
import { incidentReportingScenario } from "./incident-reporting.scenario";
import { passwordsMfaScenario } from "./passwords-mfa.scenario";
import { sharingFilesScenario } from "./sharing-files.scenario";
import { sendItSafelyScenario } from "./send-it-safely.scenario";
import { usingAiSafelyScenario } from "./using-ai-safely.scenario";
import { malwareScenario } from "./malware.scenario";
import { pauseAndVerifyScenario } from "./pause-and-verify.scenario";
import { remoteWorkByodScenario } from "./remote-work-byod.scenario";
import { messageFromItScenario } from "./message-from-it.scenario";
import { mfaFatigueScenario } from "./mfa-fatigue.scenario";
import { clickFixScenario } from "./clickfix.scenario";
import { oneClickTooManyScenario } from "./one-click-too-many.scenario";
import { invoiceThatChangedScenario } from "./invoice-that-changed.scenario";
import { the1700ReleaseScenario } from "./the-1700-release.scenario";

// DO NOT REMOVE a built-in from this map when adding a new course. Every id
// here is also a shipped Cyberfrog `_system` interactive_3d training that must
// keep loading for every tenant's /t/<token> delivery; dropping one makes the
// SPA throw `unknown scenario "<id>"` and the course silently vanishes from the
// library even though its DB row still exists. Append new courses; never swap
// one out. (This is exactly how "Spear Phishing: The Perfect Bait" went missing
// once when remote-work-max was added — restored 2026-07-11.)
// (office-explore and office-walk-poc were dev PoCs, never seeded as _system
// courses; deliberately retired 2026-07-18 in favour of office-walkable.)
export const REGISTRY: Record<string, ScenarioSpec> = {
  [phishingOfficeScenario.id]: phishingOfficeScenario,
  [deepfakeWhalingScenario.id]: deepfakeWhalingScenario,
  [socialEngineeringScenario.id]: socialEngineeringScenario,
  [quishingRestaurantScenario.id]: quishingRestaurantScenario,
  [ransomwareOfficeScenario.id]: ransomwareOfficeScenario,
  [spearPhishingOsintScenario.id]: spearPhishingOsintScenario,
  [remoteWorkMaxScenario.id]: remoteWorkMaxScenario,
  [officeWalkableScenario.id]: officeWalkableScenario,
  [officeUnderAttackScenario.id]: officeUnderAttackScenario,
  [poolWalkableScenario.id]: poolWalkableScenario,
  [holidayCallScenario.id]: holidayCallScenario,
  // Breach-notification course. Registered so `?scenario=pc-to-darkweb` runs,
  // but deliberately NOT yet listed on the public library page
  // (public/intro/bodies/library.html) or seeded as a Cyberfrog `_system`
  // training — it is in review.
  [pcToDarkWebScenario.id]: pcToDarkWebScenario,
  // Data-handling course. Registered so `?scenario=data-handling` runs,
  // but deliberately NOT yet listed on the public library page
  // (public/intro/bodies/library.html) or seeded as a Cyberfrog `_system`
  // training — it is in review.
  [dataHandlingScenario.id]: dataHandlingScenario,
  [spotPhishingScenario.id]: spotPhishingScenario,
  [workplacePoliciesScenario.id]: workplacePoliciesScenario,
  [insideTheBankScenario.id]: insideTheBankScenario,
  [incidentReportingScenario.id]: incidentReportingScenario,
  // Passwords briefing. Registered so ?scenario=passwords-mfa runs.
  // Not on the public library page until it is published.
  [passwordsMfaScenario.id]: passwordsMfaScenario,
  [sharingFilesScenario.id]: sharingFilesScenario,
  [sendItSafelyScenario.id]: sendItSafelyScenario,
  // AI-at-work briefing. Registered so ?scenario=using-ai-safely runs.
  // Not on the public library page until it is published.
  [usingAiSafelyScenario.id]: usingAiSafelyScenario,
  [malwareScenario.id]: malwareScenario,
  [pauseAndVerifyScenario.id]: pauseAndVerifyScenario,
  // Remote work & BYOD briefing. Registered so ?scenario=remote-work-byod runs.
  // Not on the public library page until it is published.
  [remoteWorkByodScenario.id]: remoteWorkByodScenario,
  // The Message From IT. Registered so `?scenario=message-from-it` runs; NOT
  // on the public library page or seeded as a Cyberfrog training (in review).
  [messageFromItScenario.id]: messageFromItScenario,
  // Approve or Deny (MFA fatigue). Registered so `?scenario=mfa-fatigue` runs;
  // NOT on the public library page or seeded as a Cyberfrog training (in review).
  [mfaFatigueScenario.id]: mfaFatigueScenario,
  // The Fake Browser Fix (ClickFix). Registered so `?scenario=clickfix` runs;
  // NOT on the public library page or seeded as a Cyberfrog training (in review).
  [clickFixScenario.id]: clickFixScenario,
  // One Click Too Many (Shadow IT / Shadow AI / malicious OAuth app), expo booth.
  [oneClickTooManyScenario.id]: oneClickTooManyScenario,
  [invoiceThatChangedScenario.id]: invoiceThatChangedScenario,
  // The 17:00 Release (developers, AI coding assistants), sci-fi desk. NOT on
  // the public library page or seeded as a Cyberfrog training (in review).
  [the1700ReleaseScenario.id]: the1700ReleaseScenario,
};

export const DEFAULT_SCENARIO_ID = phishingOfficeScenario.id;

export interface ScenarioCard {
  id: string;
  title: string;
  topic: string;
  language?: string;
}

/** Courses that ship as a published spec (not compiled into REGISTRY). */
const SPEC_ONLY_URLS: Record<string, string> = {
  "bar-walkable": "/cdn/sim/bar-walkable/bar-walkable-spec.json",
  "arcade-challenge": "/cdn/sim/arcade-hall/walk5.json",
};

export function listScenarios(): ScenarioCard[] {
  return Object.values(REGISTRY).map((s) => ({
    id: s.id,
    title: s.title,
    topic: s.topic,
    language: s.language,
  }));
}

/** True when a course is explicitly selected — by the Cyberfrog host bootstrap
 *  (window.__CF_SIM__) OR by the URL (?scenario / ?spec). When false the app
 *  shows the course library landing instead of auto-running one. */
export function hasExplicitScenario(search = window.location.search): boolean {
  const host = getHostConfig();
  if (host.hosted && (host.scenarioId || host.specUrl)) return true;
  const p = new URLSearchParams(search);
  return Boolean(p.get("scenario") || p.get("spec"));
}

/** Resolve which scenario to run. Host bootstrap (Cyberfrog) wins, then the URL
 *  (?spec remote JSON, ?scenario built-in id), then the default. */
export async function loadScenario(search = window.location.search): Promise<ScenarioSpec> {
  const host = getHostConfig();
  if (host.hosted) {
    // A remote spec (future AI-authored courses) takes priority; otherwise run
    // the named built-in shipped in this bundle (v1 library courses).
    if (host.specUrl) {
      const res = await fetch(host.specUrl, { credentials: "omit" });
      if (!res.ok) throw new Error(`failed to load scenario spec: ${res.status}`);
      return (await res.json()) as ScenarioSpec;
    }
    if (host.scenarioId) {
      const spec = REGISTRY[host.scenarioId];
      if (spec) return spec;
      // Spec-only course opened as a hub exercise (?m=<id>): same file the
      // standalone library links with ?spec=.
      const remote = SPEC_ONLY_URLS[host.scenarioId];
      if (remote) {
        const res = await fetch(remote, { credentials: "omit" });
        if (!res.ok) throw new Error(`failed to load scenario spec: ${res.status}`);
        return (await res.json()) as ScenarioSpec;
      }
      throw new Error(`unknown scenario "${host.scenarioId}"`);
    }
  }

  const params = new URLSearchParams(search);

  const specUrl = params.get("spec");
  if (specUrl) {
    const res = await fetch(specUrl, { credentials: "omit" });
    if (!res.ok) throw new Error(`failed to load scenario spec: ${res.status}`);
    return (await res.json()) as ScenarioSpec;
  }

  const id = params.get("scenario") ?? DEFAULT_SCENARIO_ID;
  const spec = REGISTRY[id];
  if (spec) return spec;
  // Spec-only courses also open by name (?scenario=<id>), like built-ins.
  const remote = SPEC_ONLY_URLS[id];
  if (remote) {
    const res = await fetch(remote, { credentials: "omit" });
    if (!res.ok) throw new Error(`failed to load scenario spec: ${res.status}`);
    return (await res.json()) as ScenarioSpec;
  }
  throw new Error(`unknown scenario "${id}"`);
}
